import React, { useEffect, useState } from "react";
import "./gameList.css";

const DUMMY_GAMES = [];

function GameList() {
  const [games, setGames] = useState(DUMMY_GAMES);
  const [filteredGames, setFilteredGames] = useState(DUMMY_GAMES);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editGameId, setEditGameId] = useState(null);

  // Dropdown data states
  const [tournaments, setTournaments] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [members, setMembers] = useState([]);
  const [tournamentFullData, setTournamentFullData] = useState(null);

  // Loading states for dropdowns
  const [loadingTournaments, setLoadingTournaments] = useState(false);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [formData, setFormData] = useState({
    tournamentId: "",
    tournamentEventId: "",
    roundId: "",
    matchDateTime: "",
    courtNumber: "",
    matchNumber: 0,
    isTeamMatch: false,
    team1Player1Id: "",
    team1Player2Id: "",
    team2Player1Id: "",
    team2Player2Id: "",
    player1Id: "",
    player2Id: "",
    status: "Scheduled"
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchGames();
    fetchTournaments();
    fetchRounds();
    fetchMembers();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredGames(games);
    } else {
      const searchLower = searchTerm.toLowerCase().trim();
      const filtered = games.filter(game => {
        const matchNumberStr = game.matchNumber?.toString() || "";
        const matchNumberMatch = matchNumberStr.includes(searchTerm.trim());
        const tournamentMatch = (game.tournamentName || "").toLowerCase().includes(searchLower);
        const eventMatch = (game.eventName || "").toLowerCase().includes(searchLower);
        const roundMatch = (game.roundName || "").toLowerCase().includes(searchLower);
        const courtMatch = (game.courtNumber || "").toLowerCase().includes(searchLower);
        const team1Match = (game.team1Name || "").toLowerCase().includes(searchLower);
        const team2Match = (game.team2Name || "").toLowerCase().includes(searchLower);
        const statusMatch = (game.status || "").toLowerCase().includes(searchLower);

        return matchNumberMatch || tournamentMatch || eventMatch || roundMatch ||
          courtMatch || team1Match || team2Match || statusMatch;
      });

      setFilteredGames(filtered);
    }
  }, [searchTerm, games]);

  const fetchTournaments = async () => {
    try {
      setLoadingTournaments(true);
      const response = await fetch("https://api.asterickx.com/api/Tournaments", {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const tournamentsArray = Array.isArray(data) ? data : (data.$values || []);
        setTournaments(tournamentsArray);
      } else if (response.status === 401) {
        alert("Authentication failed. Please login again.");
      }
    } catch (err) {
      console.error("Error fetching tournaments:", err);
    } finally {
      setLoadingTournaments(false);
    }
  };

  const fetchRounds = async () => {
    try {
      setLoadingRounds(true);
      const response = await fetch("https://api.asterickx.com/api/Rounds", {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const roundsArray = Array.isArray(data) ? data : (data.$values || []);
        const sortedRounds = roundsArray.sort((a, b) => {
          const orderA = a.sortOrder || a.SortOrder || 0;
          const orderB = b.sortOrder || b.SortOrder || 0;
          return orderA - orderB;
        });
        setRounds(sortedRounds);
      } else if (response.status === 401) {
        alert("Authentication failed. Please login again.");
      }
    } catch (err) {
      console.error("Error fetching rounds:", err);
    } finally {
      setLoadingRounds(false);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await fetch("https://api.asterickx.com/api/Members/GetAllPlayer", {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        let membersArray = [];
        if (Array.isArray(data)) {
          membersArray = data;
        } else if (data.$values && Array.isArray(data.$values)) {
          membersArray = data.$values;
        }
        setMembers(membersArray);
      } else {
        if (response.status === 401) {
          alert("Authentication failed. Please login again.");
        }
        setMembers([]);
      }
    } catch (err) {
      console.error("Error fetching members:", err);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchTournamentEvents = async (tournamentId) => {
    try {
      const response = await fetch(
        `https://api.asterickx.com/api/TournamentEvents?tournamentId=${tournamentId}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      if (response.ok) {
        const data = await response.json();
        let eventsArray = [];
        if (Array.isArray(data)) {
          eventsArray = data;
        } else if (data.$values && Array.isArray(data.$values)) {
          eventsArray = data.$values;
        }
        setTournamentFullData({ events: eventsArray });
      } else {
        if (response.status === 401) {
          alert("Authentication failed. Please login again.");
        }
        setTournamentFullData(null);
      }
    } catch (err) {
      console.error("Error fetching tournament events:", err);
      setTournamentFullData(null);
    }
  };

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://api.asterickx.com/api/Matches",
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          alert("Authentication failed. Please login again.");
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      let matchesArray = [];
      if (Array.isArray(data)) {
        matchesArray = data;
      } else if (data.$values && Array.isArray(data.$values)) {
        matchesArray = data.$values;
      }

      const mappedGames = matchesArray.map(match => {
        const isTeam = match.isTeam || match.isTeamMatch || match.IsTeamMatch || false;
        
        // Extract player names - API uses lowercase p1A, p1B, p2A, p2B
        const p1A = match.p1A || match.player1AName || match.Player1AName || "";
        const p1B = match.p1B || match.player1BName || match.Player1BName || "";
        const p2A = match.p2A || match.player2AName || match.Player2AName || "";
        const p2B = match.p2B || match.player2BName || match.Player2BName || "";
        
        let team1Display = "";
        let team2Display = "";
        
        // Always check if we have multiple players, regardless of isTeam flag
        const team1Players = [p1A, p1B].filter(name => name && name.trim() !== "");
        const team2Players = [p2A, p2B].filter(name => name && name.trim() !== "");
        
        // If we have 2 players on either side, it's a doubles match - show both
        if (team1Players.length > 1 || team2Players.length > 1 || isTeam) {
          team1Display = team1Players.length > 0 ? team1Players.join(" & ") : "TBD";
          team2Display = team2Players.length > 0 ? team2Players.join(" & ") : "TBD";
        } else {
          // Singles match - show only first player
          team1Display = p1A || "TBD";
          team2Display = p2A || "TBD";
        }

        console.log("Match processing:", {
          id: match.id || match.Id,
          isTeam,
          p1A, p1B, p2A, p2B,
          team1Players,
          team2Players,
          team1Display,
          team2Display
        });

        return {
          id: match.id || match.Id,
          matchNumber: match.matchNumber || match.MatchNumber,
          tournamentId: match.tournamentId || match.TournamentId,
          tournamentName: match.tournamentName || match.TournamentName,
          tournamentEventId: match.tournamentEventId || match.TournamentEventId,
          eventName: match.eventName || match.EventName || match.eventCategoryDisplayName || match.EventCategoryDisplayName,
          roundId: match.roundId || match.RoundId,
          roundName: match.roundName || match.RoundName,
          matchDateTime: match.matchDateTime || match.MatchDateTime,
          courtNumber: match.courtNumber || match.CourtNumber,
          team1Name: team1Display,
          team2Name: team2Display,
          player1AId: match.player1AId || match.Player1AId,
          player1BId: match.player1BId || match.Player1BId,
          player2AId: match.player2AId || match.Player2AId,
          player2BId: match.player2BId || match.Player2BId,
          isTeamMatch: isTeam,
          status: match.status || match.Status
        };
      });

      const mergedGames = mappedGames.length > 0 ? mappedGames : DUMMY_GAMES;
      setGames(mergedGames);
      setFilteredGames(mergedGames);
    } catch (err) {
      console.error("Error fetching games:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "tournamentId" && value) {
      fetchTournamentEvents(value);
      setFormData(prev => ({
        ...prev,
        tournamentId: value,
        tournamentEventId: "",
        team1Player1Id: "",
        team1Player2Id: "",
        team2Player1Id: "",
        team2Player2Id: "",
        player1Id: "",
        player2Id: "",
        isTeamMatch: false
      }));
    } else if (name === "tournamentEventId" && value) {
      const selectedCategory = getEventCategories().find(cat => cat.id === value);
      if (selectedCategory) {
        const isDoubles = selectedCategory.isDoubles || false;

        setFormData(prev => ({
          ...prev,
          [name]: value,
          isTeamMatch: isDoubles,
          team1Player1Id: "",
          team1Player2Id: "",
          team2Player1Id: "",
          team2Player2Id: "",
          player1Id: "",
          player2Id: ""
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value
      }));
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const matchData = {
        tournamentId: formData.tournamentId,
        tournamentEventId: formData.tournamentEventId,
        roundId: formData.roundId,
        matchDateTime: formData.matchDateTime,
        courtNumber: formData.courtNumber,
        matchNumber: 1,
        status: formData.status
      };

      if (formData.isTeamMatch) {
        // For doubles matches - use individual player selections
        if (formData.team1Player1Id) matchData.player1AId = formData.team1Player1Id;
        if (formData.team1Player2Id) matchData.player1BId = formData.team1Player2Id;
        if (formData.team2Player1Id) matchData.player2AId = formData.team2Player1Id;
        if (formData.team2Player2Id) matchData.player2BId = formData.team2Player2Id;
      } else {
        // For singles matches
        if (formData.player1Id) matchData.player1AId = formData.player1Id;
        if (formData.player2Id) matchData.player2AId = formData.player2Id;
      }

      const url = isEdit
        ? `https://api.asterickx.com/api/Matches/${editGameId}`
        : "https://api.asterickx.com/api/Matches";

      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify(matchData)
      });

      if (response.ok) {
        if (response.status === 204) {
          alert("Match updated successfully!");
          fetchGames();
          resetForm();
        } else {
          const responseData = await response.json();
          const matchNumber = responseData.matchNumber || responseData.MatchNumber || '';
          
          if (isEdit) {
            alert(`Match updated successfully!`);
          } else {
            alert(`Match created successfully!${matchNumber ? ' Match Number: ' + matchNumber : ''}`);
          }
          
          fetchGames();
          resetForm();
        }
      } else {
        if (response.status === 401) {
          alert("Authentication failed. Please login again.");
        } else {
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            throw new Error(errorData.message || errorData.title || 'Failed to save match');
          } catch (parseErr) {
            throw new Error(errorText || 'Failed to save match');
          }
        }
      }
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} match:`, err);
      alert(`Failed to ${isEdit ? 'update' : 'create'} match. ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (game) => {
    setIsEdit(true);
    setEditGameId(game.id);

    try {
      const response = await fetch(
        `https://api.asterickx.com/api/Matches/${game.id}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        const matchData = await response.json();

        if (matchData.tournamentId || matchData.TournamentId) {
          const tournamentId = matchData.tournamentId || matchData.TournamentId;
          await fetchTournamentEvents(tournamentId);
        }

        const isTeam = matchData.isTeamMatch || matchData.IsTeamMatch || false;

        setFormData({
          tournamentId: matchData.tournamentId || matchData.TournamentId || "",
          tournamentEventId: matchData.tournamentEventId || matchData.TournamentEventId || "",
          roundId: matchData.roundId || matchData.RoundId || "",
          matchDateTime: matchData.matchDateTime || matchData.MatchDateTime || "",
          courtNumber: matchData.courtNumber || matchData.CourtNumber || "",
          matchNumber: matchData.matchNumber || matchData.MatchNumber || 0,
          isTeamMatch: isTeam,
          team1Player1Id: matchData.player1AId || matchData.Player1AId || "",
          team1Player2Id: matchData.player1BId || matchData.Player1BId || "",
          team2Player1Id: matchData.player2AId || matchData.Player2AId || "",
          team2Player2Id: matchData.player2BId || matchData.Player2BId || "",
          player1Id: matchData.player1AId || matchData.Player1AId || "",
          player2Id: matchData.player2AId || matchData.Player2AId || "",
          status: matchData.status || matchData.Status || "Scheduled"
        });

        setShowModal(true);
      } else {
        if (response.status === 401) {
          alert("Authentication failed. Please login again.");
        } else {
          alert("Failed to load match details for editing.");
        }
      }
    } catch (err) {
      console.error("Error fetching match details:", err);
      alert("Failed to load match details for editing.");
    }
  };

  const resetForm = () => {
    setFormData({
      tournamentId: "",
      tournamentEventId: "",
      roundId: "",
      matchDateTime: "",
      courtNumber: "",
      matchNumber: 0,
      isTeamMatch: false,
      team1Player1Id: "",
      team1Player2Id: "",
      team2Player1Id: "",
      team2Player2Id: "",
      player1Id: "",
      player2Id: "",
      status: "Scheduled"
    });
    setShowModal(false);
    setIsEdit(false);
    setEditGameId(null);
    setTournamentFullData(null);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateTimeString;
    }
  };

  const getEventCategories = () => {
    if (!tournamentFullData || !tournamentFullData.events) return [];

    const eventsArray = tournamentFullData.events;

    const categories = eventsArray.map(event => {
      const eventTypeCode = event.eventTypeCode || event.EventTypeCode || '';
      const categoryCode = event.eventCategoryCode || event.EventCategoryCode || '';
      const isDoubles = eventTypeCode === 'TEAM' || categoryCode.includes('_D_') || categoryCode.includes('DOUBLES');

      return {
        id: event.id || event.Id,
        displayName: event.eventCategoryDisplayName || event.EventCategoryDisplayName || event.eventCategoryCode || 'Unnamed Event',
        code: event.eventCategoryCode || event.EventCategoryCode,
        isDoubles: isDoubles,
        eventTypeCode: eventTypeCode,
        eventTypeName: event.eventTypeName || event.EventTypeName,
        categoryName: event.eventCategoryDisplayName || event.EventCategoryDisplayName
      };
    });

    return categories;
  };

  return (
    <div className="gm-container">
      <div className="gm-header-section">
        <h2 className="gm-page-title">Match Management</h2>

        <div className="gm-right-controls">
          <div className="gm-search-container">
            <input
              type="text"
              className="gm-search-input"
              placeholder="Search matches..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <svg
              className="gm-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>

          <button
            className="gm-add-btn"
            onClick={() => {
              setIsEdit(false);
              resetForm();
              setShowModal(true);
            }}
          >
            <span className="gm-btn-icon">+</span>
            <span>Add New Match</span>
          </button>
        </div>
      </div>

      {searchTerm && (
        <div className="gm-search-results-info">
          Showing {filteredGames.length} of {games.length} matches
        </div>
      )}

      <div className="gm-table-container">
        <table className="gm-table">
          <thead>
            <tr>
              <th>Match #</th>
              <th>Tournament</th>
              <th>Event</th>
              <th>Round</th>
              <th>Date & Time</th>
              <th>Court</th>
              <th>Team/Player 1</th>
              <th>Team/Player 2</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="gm-text-center">
                  <div className="gm-loader"></div>
                  Loading...
                </td>
              </tr>
            ) : filteredGames.length > 0 ? (
              filteredGames.map((game, index) => {
                return (
                  <tr key={game.id || index}>
                    <td className="gm-match-number">{game.matchNumber || "N/A"}</td>
                    <td>{game.tournamentName || "N/A"}</td>
                    <td>{game.eventName || "N/A"}</td>
                    <td>
                      <span className={`gm-round-badge gm-round-${game.roundName?.toLowerCase().replace(/\s+/g, '-')}`}>
                        {game.roundName || "N/A"}
                      </span>
                    </td>
                    <td>{formatDateTime(game.matchDateTime)}</td>
                    <td>
                      <span className="gm-court-badge">
                        {game.courtNumber || "N/A"}
                      </span>
                    </td>
                    <td className="gm-player-cell">{game.team1Name || "N/A"}</td>
                    <td className="gm-player-cell">{game.team2Name || "N/A"}</td>
                    <td>
                      <span className={`gm-status-badge gm-status-${game.status?.toLowerCase()}`}>
                        {game.status || "N/A"}
                      </span>
                    </td>
                    <td>
                      <div className="gm-action-buttons">
                        <button
                          className="gm-edit-btn"
                          onClick={() => handleEdit(game)}
                          title="Edit Match"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                            <path d="m15 5 4 4"></path>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="10" className="gm-text-center">
                  {searchTerm ? "No matches found matching your search" : "No matches available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit/Add Modal */}
      {showModal && (
        <div className="gm-modal-overlay" onClick={(e) => {
          if (e.target.className === "gm-modal-overlay") {
            resetForm();
          }
        }}>
          <div className="gm-modal-container">
            <div className="gm-modal-header">
              <h3>{isEdit ? "Edit Match" : "Add New Match"}</h3>
              <button className="gm-close-btn" onClick={resetForm}>×</button>
            </div>

            <div className="gm-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="gm-form-grid">
                  {/* Tournament Dropdown */}
                  <div className="gm-form-group">
                    <label>Tournament *</label>
                    <select
                      name="tournamentId"
                      value={formData.tournamentId}
                      onChange={handleInputChange}
                      required
                      disabled={loadingTournaments}
                    >
                      <option value="">Select Tournament</option>
                      {tournaments.map(tournament => (
                        <option key={tournament.id || tournament.Id} value={tournament.id || tournament.Id}>
                          {tournament.name || tournament.Name || 'Unnamed Tournament'}
                        </option>
                      ))}
                    </select>
                    {loadingTournaments && <small>Loading tournaments...</small>}
                  </div>

                  {/* Tournament Event Dropdown */}
                  <div className="gm-form-group">
                    <label>Tournament Event *</label>
                    <select
                      name="tournamentEventId"
                      value={formData.tournamentEventId}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.tournamentId || getEventCategories().length === 0}
                    >
                      <option value="">Select Event</option>
                      {getEventCategories().map(category => (
                        <option key={category.id} value={category.id}>
                                                                        
                          {category.displayName} 
                          {/* {category.isDoubles ? '(Doubles)' : '(Singles)'} */}
                        </option>
                      ))}
                    </select>
                    {formData.tournamentId && getEventCategories().length === 0 && (
                      <small style={{ color: '#999' }}>No events available for this tournament</small>
                    )}
                    {!formData.tournamentId && (
                      <small style={{ color: '#999' }}>Please select a tournament first</small>
                    )}
                  </div>

                  {/* Round Dropdown */}
                  <div className="gm-form-group">
                    <label>Round *</label>
                    <select
                      name="roundId"
                      value={formData.roundId}
                      onChange={handleInputChange}
                      required
                      disabled={loadingRounds}
                    >
                      <option value="">Select Round</option>
                      {rounds.map(round => {
                        const id = round.id || round.Id;
                        const displayName = round.displayName || round.DisplayName || round.name || round.Name;
                        const isQualifying = round.isQualifying || round.IsQualifying;

                        return (
                          <option key={id} value={id}>
                            {displayName} {isQualifying ? '(Q)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {loadingRounds && <small>Loading rounds...</small>}
                  </div>

                  {/* Match Date & Time */}
                  <div className="gm-form-group">
                    <label>Match Date & Time *</label>
                    <input
                      type="datetime-local"
                      name="matchDateTime"
                      value={formData.matchDateTime}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* Court Number */}
                  <div className="gm-form-group">
                    <label>Court Number *</label>
                    <input
                      type="text"
                      name="courtNumber"
                      value={formData.courtNumber}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Court 1"
                    />
                  </div>

                  {/* Status */}
                  <div className="gm-form-group">
                    <label>Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Postponed">Postponed</option>
                    </select>
                  </div>

                  {/* Match Type Info */}
                  {formData.tournamentEventId && (
                    <div className="gm-form-group gm-full-width">
                      <div style={{
                        padding: '12px',
                        backgroundColor: formData.isTeamMatch ? '#f3e5f5' : '#e3f2fd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: formData.isTeamMatch ? '#6a1b9a' : '#1976d2'
                      }}>
                        {formData.isTeamMatch ? '🎾 Doubles Match - Select 2 Players for Each Side' : '🏸 Singles Match - Select Individual Players'}
                      </div>
                    </div>
                  )}

                  {/* Doubles Match Fields */}
                  {formData.isTeamMatch ? (
                    <>
                      <div className="gm-form-group gm-full-width gm-team-header">
                        <h4>Side 1 Players</h4>
                      </div>

                      <div className="gm-form-group">
                        <label>Side 1 - Player 1 *</label>
                        <select
                          name="team1Player1Id"
                          value={formData.team1Player1Id}
                          onChange={handleInputChange}
                          required
                          disabled={loadingMembers}
                        >
                          <option value="">Select Player</option>
                          {members.map(member => {
                            const fullName = member.fullName || member.FullName || 
                                           `${member.firstName || member.FirstName || ''} ${member.lastName || member.LastName || ''}`.trim();
                            const regNumber = member.registrationNumber || member.RegistrationNumber || 'N/A';
                            const genderName = member.genderName || member.GenderName || member.gender || member.Gender || '';
                            
                            return (
                              <option key={member.id || member.Id} value={member.id || member.Id}>
                                {fullName} - Reg#{regNumber} {genderName ? `(${genderName})` : ''}
                              </option>
                            );
                          })}
                        </select>
                        {loadingMembers && <small>Loading members...</small>}
                        {!loadingMembers && members.length > 0 && (
                          <small style={{ color: '#388e3c' }}>{members.length} member(s) available</small>
                        )}
                      </div>

                      <div className="gm-form-group">
                        <label>Side 1 - Player 2 *</label>
                        <select
                          name="team1Player2Id"
                          value={formData.team1Player2Id}
                          onChange={handleInputChange}
                          required
                          disabled={loadingMembers}
                        >
                          <option value="">Select Player</option>
                          {members.map(member => {
                            const fullName = member.fullName || member.FullName || 
                                           `${member.firstName || member.FirstName || ''} ${member.lastName || member.LastName || ''}`.trim();
                            const regNumber = member.registrationNumber || member.RegistrationNumber || 'N/A';
                            const genderName = member.genderName || member.GenderName || member.gender || member.Gender || '';
                            
                            return (
                              <option key={member.id || member.Id} value={member.id || member.Id}>
                                {fullName} - Reg#{regNumber} {genderName ? `(${genderName})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="gm-form-group gm-full-width gm-team-header">
                        <h4>Side 2 Players</h4>
                      </div>

                      <div className="gm-form-group">
                        <label>Side 2 - Player 1 *</label>
                        <select
                          name="team2Player1Id"
                          value={formData.team2Player1Id}
                          onChange={handleInputChange}
                          required
                          disabled={loadingMembers}
                        >
                          <option value="">Select Player</option>
                          {members.map(member => {
                            const fullName = member.fullName || member.FullName || 
                                           `${member.firstName || member.FirstName || ''} ${member.lastName || member.LastName || ''}`.trim();
                            const regNumber = member.registrationNumber || member.RegistrationNumber || 'N/A';
                            const genderName = member.genderName || member.GenderName || member.gender || member.Gender || '';
                            
                            return (
                              <option key={member.id || member.Id} value={member.id || member.Id}>
                                {fullName} - Reg#{regNumber} {genderName ? `(${genderName})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="gm-form-group">
                        <label>Side 2 - Player 2 *</label>
                        <select
                          name="team2Player2Id"
                          value={formData.team2Player2Id}
                          onChange={handleInputChange}
                          required
                          disabled={loadingMembers}
                        >
                          <option value="">Select Player</option>
                          {members.map(member => {
                            const fullName = member.fullName || member.FullName || 
                                           `${member.firstName || member.FirstName || ''} ${member.lastName || member.LastName || ''}`.trim();
                            const regNumber = member.registrationNumber || member.RegistrationNumber || 'N/A';
                            const genderName = member.genderName || member.GenderName || member.gender || member.Gender || '';
                            
                            return (
                              <option key={member.id || member.Id} value={member.id || member.Id}>
                                {fullName} - Reg#{regNumber} {genderName ? `(${genderName})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Singles Match Fields */}
                      <div className="gm-form-group gm-full-width">
                        <label>Player 1 *</label>
                        <select
                          name="player1Id"
                          value={formData.player1Id}
                          onChange={handleInputChange}
                          required
                          disabled={loadingMembers}
                        >
                          <option value="">Select Player</option>
                          {members.map(member => {
                            const fullName = member.fullName || member.FullName || 
                                           `${member.firstName || member.FirstName || ''} ${member.lastName || member.LastName || ''}`.trim();
                            const regNumber = member.registrationNumber || member.RegistrationNumber || 'N/A';
                            const genderName = member.genderName || member.GenderName || member.gender || member.Gender || '';
                            
                            return (
                              <option key={member.id || member.Id} value={member.id || member.Id}>
                                {fullName} - Reg#{regNumber} {genderName ? `(${genderName})` : ''}
                              </option>
                            );
                          })}
                        </select>
                        {loadingMembers && <small>Loading members...</small>}
                        {!loadingMembers && members.length > 0 && (
                          <small style={{ color: '#388e3c' }}>{members.length} member(s) available</small>
                        )}
                        {!loadingMembers && members.length === 0 && (
                          <small style={{ color: '#e65100' }}>No members registered</small>
                        )}
                      </div>

                      <div className="gm-form-group gm-full-width">
                        <label>Player 2 *</label>
                        <select
                          name="player2Id"
                          value={formData.player2Id}
                          onChange={handleInputChange}
                          required
                          disabled={loadingMembers}
                        >
                          <option value="">Select Player</option>
                          {members.map(member => {
                            const fullName = member.fullName || member.FullName || 
                                           `${member.firstName || member.FirstName || ''} ${member.lastName || member.LastName || ''}`.trim();
                            const regNumber = member.registrationNumber || member.RegistrationNumber || 'N/A';
                            const genderName = member.genderName || member.GenderName || member.gender || member.Gender || '';
                            
                            return (
                              <option key={member.id || member.Id} value={member.id || member.Id}>
                                {fullName} - Reg#{regNumber} {genderName ? `(${genderName})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </>
                  )}
                </div>

                <div className="gm-form-actions">
                  <button type="button" className="gm-cancel-btn" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="gm-submit-btn" disabled={submitting}>
                    {submitting ? "Processing..." : (isEdit ? "Update Match" : "Create Match")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GameList;